import React, { createContext, useContext, FC } from 'react';
import useDatesStringByLocale from 'clientState/hooks/useDatesStringByLocale';
import { PillStatus } from '../FiltersPill/FiltersPill';
import { useSemMonths } from 'clientState/providers/ParsedSearchResponseProvider';
import { FilterPillSelectorOption } from '../FilterPillSelector/FilterPillSelector';

type SemMonthFilterContextType = {
  disabled: number[];
  months: FilterPillSelectorOption[];
  monthsSelected: number[];
  isSemMonThFilterApplied: boolean;
  resetMonthsFilter: () => void;
  applySemMonThFilter: (months: number[]) => void;
};

export const SemMonthFilterContext = createContext<SemMonthFilterContextType>(
  {} as SemMonthFilterContextType
);

export const useSemMonthFilter = (): SemMonthFilterContextType =>
  useContext(SemMonthFilterContext);

const generateMonths = (): number[] => {
  const currentMonth = new Date().getMonth();
  return Array.from({ length: 6 }, (_, i) => (currentMonth + i) % 12);
};

const SemMonthFilterProvider: FC = ({ children }) => {
  const { getMonth } = useDatesStringByLocale();
  const semMonths = useSemMonths();
  const [monthsSelected, setMonthsSelected] = React.useState<number[]>([]);
  const [isSemMonThFilterApplied, setSemMonthFilterApplied] =
    React.useState<boolean>(false);

  const applySemMonThFilter = (months: number[]): void => {
    setMonthsSelected(months);
    setSemMonthFilterApplied(months.length > 0);
  };

  const resetMonthsFilter = (): void => {
    setMonthsSelected([]);
    setSemMonthFilterApplied(false);
  };

  if (!semMonths)
    return (
      <SemMonthFilterContext.Provider
        value={{
          disabled: [],
          months: [],
          monthsSelected: [],
          isSemMonThFilterApplied,
          resetMonthsFilter,
          applySemMonThFilter,
        }}
      >
        {children}
      </SemMonthFilterContext.Provider>
    );

  const monthOptions = generateMonths();
  const disabled = monthOptions.filter((month) => !semMonths.has(month));

  const months: FilterPillSelectorOption[] = generateMonths().map((month) => {
    return {
      id: month,
      label: getMonth(month),
      status: PillStatus.default,
    };
  });

  const value = {
    disabled,
    months,
    monthsSelected,
    isSemMonThFilterApplied,
    resetMonthsFilter,
    applySemMonThFilter,
  };

  return (
    <SemMonthFilterContext.Provider value={value}>
      {children}
    </SemMonthFilterContext.Provider>
  );
};

export default SemMonthFilterProvider;
